import { useState } from "react";

import { ConsentCheckbox } from "@ui/components/ContactForm";
import { Form, useForm } from "@ui/components/Form";
import { Recaptchabar } from "@ui/components/Recaptchabar";
import { RichText } from "@ui/eva-components/RichText";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import { getFirstBackendError } from "@ui/utils/backendError";
import { displayToastError } from "@ui/utils/displayToastError";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { SnpApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { EmailInputProps } from "./components";
import { EmailInput } from "./components";

import styles from "./SubscribeForm.module.scss";

export type SubscribeFormProps = {
  title: string;
  subTitle: string;
  description: string;
  emailInputProps: EmailInputProps;
  successMessage: {
    title: string;
    subTitle: string;
  };
  reCaptchaEnabled: boolean;
  brandingInformationEnabled: boolean;
  ctaLabel: string;
};

type SubscribeFormStructure = {
  email: string;
  consents: Record<string, { checked: boolean; language?: string }>;
};

type formStage = "clean" | "success" | "fail";

export const SubscribeForm = ({
  description,
  emailInputProps,
  successMessage,
  ctaLabel = "Subscribe",
  subTitle,
  title,
}: SubscribeFormProps) => {
  const { dictionary, __ } = useTranslations();
  const locale = useLocale();
  const { reCaptchaEnabled, brandingInformationEnabled } = useConfig();
  const [currentFormState, setCurrentFormState] = useState<formStage>("clean");

  const form = useForm<SubscribeFormStructure>({
    defaultValues: {
      email: "",
      consents: {},
    },
  });

  const { formState } = form;

  const onSubmit = async () => {
    const dirtyConsents = Object.keys(formState.dirtyFields.consents ?? {});
    const { email, consents } = form.getValues();

    const formattedConsents = dirtyConsents.map((id) => ({
      id,
      accepted: consents[id].checked ?? false,
    }));

    try {
      const payload = {
        email,
        topics: ["marketing"],
        preferredLanguage: locale.substring(0, 2) ?? consents[formattedConsents[0]?.id]?.language,
        data_processing_agreement: formattedConsents.every((checkbox) => checkbox.accepted),
        consent_purposes: formattedConsents,
      };
      dispatchTrackingEvent(TrackingEvents.TrackingSubscribeBlockClick);
      await SnpApi.init().subscribe(payload);
      setCurrentFormState("success");
    } catch (error) {
      const backendError = getFirstBackendError(error);
      displayToastError(backendError, undefined, dictionary);
    }
  };

  const isSuccessFullySubmitted = currentFormState === "success";

  const { breakpoint } = useDeviceBreakpoint();

  const buttonSize = breakpoint === "desktop" ? "md" : "sm";

  return (
    <div className={classNames(styles.subscribeFormContainer, "subscribe-form-container")}>
      <h3 className={styles.subscribeFormTitle}>{title}</h3>
      <div className={styles.textContent}>
        <h4 className={styles.subscribeFormSubTitle}>{subTitle}</h4>
        <RichText className={styles.subscribeFormDescription} content={description} />
      </div>
      {isSuccessFullySubmitted && (
        <AlertBanner
          title={successMessage.title}
          tone="success"
          isDismissible
          onDismiss={() => setCurrentFormState("clean")}
          dismissLabel={__("subscribe_form.success_message.close_button")}
          description={successMessage.subTitle}
        />
      )}
      <Form form={form} onSubmit={onSubmit}>
        {!isSuccessFullySubmitted && (
          <div className={styles.formContainer}>
            <div className={classNames(styles.submitSection, "submit-section")}>
              <div className={classNames(styles.inputContainer, "input-container")}>
                <EmailInput {...emailInputProps} />
              </div>
              <Button
                size={buttonSize}
                disabled={!formState.isValid}
                pending={formState.isSubmitting}
                className={classNames(styles.submitBtn, "submit-btn")}
                data-testid="subscribeFormSubmit"
                type="submit"
              >
                {ctaLabel}
              </Button>
            </div>
            <div className={classNames(styles.formColumn, "form-column")}>
              <ConsentCheckbox
                consent="subscription"
                displayConsent="always"
                namePrefix="consents"
                showRequiredAsterisk={true}
              />
            </div>
            {reCaptchaEnabled && brandingInformationEnabled && (
              <div className={styles.subscribeFormRecaptcha}>
                <Recaptchabar />
              </div>
            )}
          </div>
        )}
      </Form>
    </div>
  );
};
